'use client'

import { styles } from './DebugPanel.styles'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useDebugContext } from '../context'
import type { HydrationError, LogLevel, NetworkRequest, UserAction } from '../types'
import { disableDebugger, formatTimestamp, truncateString } from '../utils'

type TabType = 'logs' | 'network' | 'actions' | 'hydration' | 'controls'

interface TabVisibility {
	logs: boolean
	network: boolean
	actions: boolean
	hydration: boolean
}

const DEFAULT_TAB_VISIBILITY: TabVisibility = {
	logs: true,
	network: true,
	actions: false,
	hydration: false
}

const TAB_VISIBILITY_COOKIE = 'debug_panel_tabs'

const getTabVisibilityFromCookie = (): TabVisibility => {
	if (typeof document === 'undefined') return DEFAULT_TAB_VISIBILITY
	const cookie = document.cookie.split('; ').find(row => row.startsWith(TAB_VISIBILITY_COOKIE + '='))
	if (!cookie) return DEFAULT_TAB_VISIBILITY
	try {
		return { ...DEFAULT_TAB_VISIBILITY, ...JSON.parse(decodeURIComponent(cookie.split('=')[1])) }
	} catch {
		return DEFAULT_TAB_VISIBILITY
	}
}

const setTabVisibilityCookie = (visibility: TabVisibility): void => {
	if (typeof document === 'undefined') return
	const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString()
	document.cookie = `${TAB_VISIBILITY_COOKIE}=${encodeURIComponent(
		JSON.stringify(visibility)
	)}; expires=${expires}; path=/`
}

interface Position {
	x: number
	y: number
}

const copyToClipboard = async (text: string): Promise<boolean> => {
	try {
		await navigator.clipboard.writeText(text)
		return true
	} catch {
		return false
	}
}

interface SourceLocation {
	component: string
	file?: string
	line?: number
	column?: number
}

const parseSourceLocations = (message: string): SourceLocation[] => {
	const locations: SourceLocation[] = []
	const seen = new Set<string>()
	let match

	const webpackRegex = /webpack-internal:\/\/\/\.\/((src|pages|lib|components)\/[^:]+\.[tj]sx?):(\d+):(\d+)/g
	while ((match = webpackRegex.exec(message)) !== null) {
		const [, file, , line, column] = match
		if (!seen.has(file)) {
			seen.add(file)
			const fileName = file.split('/').pop() || ''
			const component = fileName.replace(/\.[tj]sx?$/, '')
			locations.push({
				component,
				file,
				line: parseInt(line, 10),
				column: parseInt(column, 10)
			})
		}
	}

	const directPathRegex = /\(([^()]*\/(src|pages|lib|components)\/[^:]+\.[tj]sx?):(\d+):(\d+)\)/g
	while ((match = directPathRegex.exec(message)) !== null) {
		const [, fullPath, , line, column] = match
		const srcMatch = fullPath.match(/((?:src|pages|lib|components)\/[^:]+\.[tj]sx?)$/)
		if (srcMatch) {
			const file = srcMatch[1]
			if (!seen.has(file)) {
				seen.add(file)
				const fileName = file.split('/').pop() || ''
				const component = fileName.replace(/\.[tj]sx?$/, '')
				locations.push({
					component,
					file,
					line: parseInt(line, 10),
					column: parseInt(column, 10)
				})
			}
		}
	}

	const atRegex = /at\s+([A-Z][a-zA-Z0-9_]*)\s/g
	while ((match = atRegex.exec(message)) !== null) {
		const component = match[1]
		if (
			!seen.has(component) &&
			component !== 'Object' &&
			component !== 'Module' &&
			component !== 'Array' &&
			component !== 'Function'
		) {
			seen.add(component)
			locations.push({ component })
		}
	}

	return locations.slice(0, 15)
}

const openInEditor = (file: string, line?: number, column?: number): void => {
	const basePath = '/Users/isamusenko/Projects/frontend'
	const fullPath = file.startsWith('/') ? file : `${basePath}/${file}`
	let url = `webstorm://open?file=${encodeURIComponent(fullPath)}`
	if (line) {
		url += `&line=${line}`
		if (column) {
			url += `&column=${column}`
		}
	}
	window.open(url, '_blank')
}

const LogEntryDetails = ({ message }: { message: string }): JSX.Element | null => {
	const sourceLocations = parseSourceLocations(message)

	if (sourceLocations.length === 0) return null

	const locationsWithFiles = sourceLocations.filter(loc => loc.file)
	if (locationsWithFiles.length === 0) return null

	return (
		<div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #444' }}>
			<div style={{ color: '#888', fontSize: '10px', marginBottom: '4px' }}>📍 Источник:</div>
			<div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
				{locationsWithFiles.slice(0, 5).map((loc, i) => (
					<button
						key={i}
						onClick={() => loc.file && openInEditor(loc.file, loc.line, loc.column)}
						style={{
							background: 'none',
							border: '1px solid #555',
							borderRadius: '4px',
							color: '#4fc3f7',
							padding: '2px 6px',
							fontSize: '10px',
							cursor: 'pointer'
						}}
						title={`Открыть ${loc.file}:${loc.line} в WebStorm`}>
						{loc.component}: {loc.file?.split('/').pop()}:{loc.line}
					</button>
				))}
			</div>
		</div>
	)
}

const LogsTab = (): JSX.Element => {
	const { state, clearLogs } = useDebugContext()
	const [filter, setFilter] = useState<LogLevel | 'all'>('log')
	const [copiedId, setCopiedId] = useState<string | null>(null)
	const [expandedId, setExpandedId] = useState<string | null>(null)

	const filteredLogs = useMemo(() => {
		if (filter === 'all') return state.logs
		return state.logs.filter(log => log.level === filter)
	}, [state.logs, filter])

	const getLogStyle = (level: LogLevel) => ({
		...styles.logEntry,
		...(level === 'log' ? styles.logLog : level === 'warn' ? styles.logWarn : styles.logError)
	})

	const handleCopyLog = useCallback(async (log: (typeof state.logs)[0], e: React.MouseEvent) => {
		e.stopPropagation()
		const text = `[${log.level.toUpperCase()}] ${formatTimestamp(log.timestamp)}\n${log.message}${
			log.source ? `\nSource: ${log.source}` : ''
		}`
		const success = await copyToClipboard(text)
		if (success) {
			setCopiedId(log.id)
			setTimeout(() => setCopiedId(null), 1500)
		}
	}, [])

	const handleCopyAllLogs = useCallback(async () => {
		const text = filteredLogs
			.map(
				log =>
					`[${log.level.toUpperCase()}] ${formatTimestamp(log.timestamp)}\n${log.message}${
						log.source ? `\nSource: ${log.source}` : ''
					}`
			)
			.join('\n\n---\n\n')
		const success = await copyToClipboard(text)
		if (success) {
			setCopiedId('all')
			setTimeout(() => setCopiedId(null), 1500)
		}
	}, [filteredLogs])

	const hasSourceLinks = (message: string): boolean => {
		return parseSourceLocations(message).some(loc => loc.file)
	}

	return (
		<>
			<div style={styles.filterBar}>
				{(['all', 'log', 'warn', 'error'] as const).map(type => (
					<button
						key={type}
						style={{
							...styles.filterButton,
							...(filter === type ? styles.filterButtonActive : {})
						}}
						onClick={() => setFilter(type)}>
						{type.toUpperCase()}
					</button>
				))}
				<button style={styles.copyAllButton} onClick={handleCopyAllLogs} title="Copy all logs">
					{copiedId === 'all' ? '✓' : 'Copy All'}
				</button>
				<button style={{ ...styles.filterButton, marginLeft: 'auto' }} onClick={clearLogs}>
					Почистить
				</button>
			</div>
			<div style={styles.content}>
				{filteredLogs.length === 0 ? (
					<div style={styles.emptyState}>Логов нет, пока</div>
				) : (
					filteredLogs.map(log => {
						const isExpanded = expandedId === log.id
						const canExpand = hasSourceLinks(log.message)

						return (
							<div
								key={log.id}
								style={{
									...getLogStyle(log.level),
									...styles.logEntryWrapper,
									cursor: canExpand ? 'pointer' : 'default'
								}}
								onClick={() => canExpand && setExpandedId(isExpanded ? null : log.id)}>
								<div style={styles.logContent}>
									<span style={styles.logTime}>{formatTimestamp(log.timestamp)}</span>
									<span>
										{canExpand && <span style={{ marginRight: '4px' }}>{isExpanded ? '▼' : '▶'}</span>}
										{truncateString(log.message, 200)}
									</span>
									{log.source && <span style={styles.logSource}>{log.source}</span>}
								</div>
								<button style={styles.copyButton} onClick={e => handleCopyLog(log, e)} title="Copy log">
									{copiedId === log.id ? '✓' : '📋'}
								</button>
								{isExpanded && <LogEntryDetails message={log.message} />}
							</div>
						)
					})
				)}
			</div>
		</>
	)
}

const formatJson = (str: string): string => {
	try {
		const parsed = JSON.parse(str)
		return JSON.stringify(parsed, null, 2)
	} catch {
		return str
	}
}

const CollapsibleHeaders = ({ label, headers }: { label: string; headers: Record<string, string> }): JSX.Element => {
	const [isOpen, setIsOpen] = useState(false)

	if (Object.keys(headers).length === 0) return <></>

	return (
		<div style={styles.networkDetailsSection}>
			<button
				style={styles.headersToggle}
				onClick={e => {
					e.stopPropagation()
					setIsOpen(!isOpen)
				}}>
				<span style={styles.headersToggleIcon}>{isOpen ? '▼' : '▶'}</span>
				<span style={styles.networkDetailsLabel}>{label}</span>
				<span style={styles.headersCount}>({Object.keys(headers).length})</span>
			</button>
			{isOpen && (
				<pre style={styles.networkDetailsContent}>
					{Object.entries(headers)
						.map(([key, value]) => `${key}: ${value}`)
						.join('\n')}
				</pre>
			)}
		</div>
	)
}

const NetworkDetails = ({ request }: { request: NetworkRequest }): JSX.Element => {
	return (
		<div style={styles.networkDetails} onClick={e => e.stopPropagation()}>
			{request.requestBody && (
				<div style={styles.networkDetailsSection}>
					<span style={styles.networkDetailsLabel}>Request Body</span>
					<pre style={styles.networkDetailsContent}>{formatJson(request.requestBody)}</pre>
				</div>
			)}
			{request.responseBody && (
				<div style={styles.networkDetailsSection}>
					<span style={styles.networkDetailsLabel}>Response Body</span>
					<pre style={styles.networkDetailsContent}>{formatJson(request.responseBody)}</pre>
				</div>
			)}
			{request.error && (
				<div style={styles.networkDetailsSection}>
					<span style={styles.networkDetailsLabel}>Ошибка</span>
					<pre style={{ ...styles.networkDetailsContent, color: '#f44336' }}>{request.error}</pre>
				</div>
			)}
			{request.requestHeaders && <CollapsibleHeaders label="Request Headers" headers={request.requestHeaders} />}
			{request.responseHeaders && <CollapsibleHeaders label="Response Headers" headers={request.responseHeaders} />}
		</div>
	)
}

const NetworkTab = (): JSX.Element => {
	const { state, clearNetworkRequests } = useDebugContext()
	const [expandedId, setExpandedId] = useState<string | null>(null)

	const getStatusStyle = (request: NetworkRequest) => {
		if (request.pending) return styles.statusPending
		if (request.error || (request.status && request.status >= 400)) return styles.statusError
		return styles.statusSuccess
	}

	const getMethodColor = (method: string): string => {
		const colors: Record<string, string> = {
			GET: '#61affe',
			POST: '#49cc90',
			PUT: '#fca130',
			DELETE: '#f93e3e',
			PATCH: '#50e3c2'
		}
		return colors[method] || '#888'
	}

	const toggleExpand = useCallback((id: string) => {
		setExpandedId(prev => (prev === id ? null : id))
	}, [])

	return (
		<>
			<div style={styles.filterBar}>
				<button style={{ ...styles.filterButton, marginLeft: 'auto' }} onClick={clearNetworkRequests}>
					Clear
				</button>
			</div>
			<div style={styles.content}>
				{state.networkRequests.length === 0 ? (
					<div style={styles.emptyState}>No network requests yet</div>
				) : (
					state.networkRequests.map(request => (
						<div
							key={request.id}
							style={{ ...styles.networkEntry, ...styles.networkEntryClickable }}
							onClick={() => toggleExpand(request.id)}>
							<div style={styles.networkEntryHeader}>
								<span
									style={{
										...styles.networkMethod,
										color: getMethodColor(request.method)
									}}>
									{request.method}
								</span>
								<span style={{ ...styles.networkStatus, ...getStatusStyle(request) }}>
									{request.pending ? '⏳ Pending' : request.status || 'ERR'}
								</span>
								<span style={styles.networkDuration}>{request.duration ? `${request.duration}ms` : ''}</span>
								<span style={styles.expandIcon}>{expandedId === request.id ? '▼' : '▶'}</span>
							</div>
							<span style={styles.networkUrl}>{request.url}</span>
							{expandedId === request.id && <NetworkDetails request={request} />}
						</div>
					))
				)}
			</div>
		</>
	)
}

const ActionsTab = (): JSX.Element => {
	const { state, clearUserActions } = useDebugContext()

	const getActionTypeStyle = (type: UserAction['type']) => ({
		...styles.actionType,
		...(type === 'click' ? styles.actionClick : type === 'submit' ? styles.actionSubmit : styles.actionNavigation)
	})

	return (
		<>
			<div style={styles.filterBar}>
				<button style={{ ...styles.filterButton, marginLeft: 'auto' }} onClick={clearUserActions}>
					Почистить
				</button>
			</div>
			<div style={styles.content}>
				{state.userActions.length === 0 ? (
					<div style={styles.emptyState}>Действий нет</div>
				) : (
					state.userActions.map(action => (
						<div key={action.id} style={styles.actionEntry}>
							<span style={styles.logTime}>{formatTimestamp(action.timestamp)}</span>
							<span style={getActionTypeStyle(action.type)}>{action.type}</span>
							<span>{truncateString(action.target, 50)}</span>
						</div>
					))
				)}
			</div>
		</>
	)
}

const HydrationErrorDetails = ({ error }: { error: HydrationError }): JSX.Element => {
	const [isStackOpen, setIsStackOpen] = useState(false)
	const [isMessageOpen, setIsMessageOpen] = useState(true)

	const handleToggleMessage = (e: React.MouseEvent): void => {
		e.stopPropagation()
		setIsMessageOpen(!isMessageOpen)
	}

	const handleToggleStack = (e: React.MouseEvent): void => {
		e.stopPropagation()
		setIsStackOpen(!isStackOpen)
	}

	const sourceLocations = parseSourceLocations(error.message + (error.componentStack || ''))

	const handleOpenFile = (e: React.MouseEvent, loc: SourceLocation): void => {
		e.stopPropagation()
		if (loc.file) {
			openInEditor(loc.file, loc.line, loc.column)
		}
	}

	return (
		<div style={styles.networkDetails} onClick={e => e.stopPropagation()}>
			{sourceLocations.length > 0 && (
				<div style={styles.networkDetailsSection}>
					<span style={{ ...styles.networkDetailsLabel, color: '#2196f3' }}>📍 Путь к компоненту</span>
					<div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
						{sourceLocations.map((loc, i) => (
							<div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
								<span
									style={{
										backgroundColor: '#2196f3',
										color: '#fff',
										padding: '2px 8px',
										borderRadius: '4px',
										fontSize: '11px',
										fontWeight: 'bold',
										minWidth: '80px',
										textAlign: 'center'
									}}>
									{loc.component}
								</span>
								{loc.file && (
									<button
										onClick={e => handleOpenFile(e, loc)}
										style={{
											background: 'none',
											border: '1px solid #666',
											borderRadius: '4px',
											color: '#4fc3f7',
											padding: '2px 8px',
											fontSize: '10px',
											cursor: 'pointer',
											textDecoration: 'underline'
										}}
										title={`Открыть ${loc.file}:${loc.line} в VSCode`}>
										{loc.file.split('/').pop()}:{loc.line}
									</button>
								)}
							</div>
						))}
					</div>
				</div>
			)}
			{error.serverContent && (
				<div style={styles.networkDetailsSection}>
					<span style={{ ...styles.networkDetailsLabel, color: '#4caf50' }}>Сервер</span>
					<pre style={{ ...styles.networkDetailsContent, backgroundColor: '#1a3a1a', border: '1px solid #4caf50' }}>
						{error.serverContent}
					</pre>
				</div>
			)}
			{error.clientContent && (
				<div style={styles.networkDetailsSection}>
					<span style={{ ...styles.networkDetailsLabel, color: '#f44336' }}>Клиент</span>
					<pre style={{ ...styles.networkDetailsContent, backgroundColor: '#3a1a1a', border: '1px solid #f44336' }}>
						{error.clientContent}
					</pre>
				</div>
			)}
			<div style={styles.networkDetailsSection}>
				<button style={styles.headersToggle} onClick={handleToggleMessage}>
					<span style={styles.headersToggleIcon}>{isMessageOpen ? '▼' : '▶'}</span>
					<span>Полное сообщение</span>
				</button>
				{isMessageOpen && (
					<pre
						style={{
							...styles.networkDetailsContent,
							fontSize: '11px',
							whiteSpace: 'pre-wrap',
							wordBreak: 'break-word',
							color: '#ffcc00',
							maxHeight: '300px',
							overflow: 'auto'
						}}>
						{error.message}
					</pre>
				)}
			</div>
			{error.componentStack && (
				<div style={styles.networkDetailsSection}>
					<button style={styles.headersToggle} onClick={handleToggleStack}>
						<span style={styles.headersToggleIcon}>{isStackOpen ? '▼' : '▶'}</span>
						<span>Component Stack</span>
					</button>
					{isStackOpen && (
						<pre
							style={{
								...styles.networkDetailsContent,
								fontSize: '11px',
								color: '#aaa',
								maxHeight: '200px',
								overflow: 'auto'
							}}>
							{error.componentStack}
						</pre>
					)}
				</div>
			)}
		</div>
	)
}

const HydrationTab = (): JSX.Element => {
	const { state, clearHydrationErrors } = useDebugContext()
	const [expandedId, setExpandedId] = useState<string | null>(null)

	return (
		<>
			<div style={styles.filterBar}>
				<span style={{ color: '#f44336', fontWeight: 'bold' }}>
					{state.hydrationErrors.length > 0 ? `⚠️ ${state.hydrationErrors.length} ошибок` : '✅ Ошибок нет'}
				</span>
				<button style={{ ...styles.filterButton, marginLeft: 'auto' }} onClick={clearHydrationErrors}>
					Почистить
				</button>
			</div>
			<div style={styles.content}>
				{state.hydrationErrors.length === 0 ? (
					<div style={styles.emptyState}>Ошибок гидратации не обнаружено</div>
				) : (
					state.hydrationErrors.map(error => (
						<div
							key={error.id}
							style={{
								...styles.networkEntry,
								borderLeft: '3px solid #f44336',
								cursor: 'pointer'
							}}
							onClick={() => setExpandedId(expandedId === error.id ? null : error.id)}>
							<div style={styles.networkEntryHeader}>
								<span style={{ ...styles.networkMethod, backgroundColor: '#f44336' }}>ERROR</span>
								<span style={styles.networkTime}>{formatTimestamp(error.timestamp)}</span>
								<span style={styles.expandIcon}>{expandedId === error.id ? '▼' : '▶'}</span>
							</div>
							<span style={{ ...styles.networkUrl, color: '#f44336' }}>{truncateString(error.message, 100)}</span>
							{expandedId === error.id && <HydrationErrorDetails error={error} />}
						</div>
					))
				)}
			</div>
		</>
	)
}

interface ControlsTabProps {
	tabVisibility: TabVisibility
	onTabVisibilityChange: (visibility: TabVisibility) => void
}

const ControlsTab = ({ tabVisibility, onTabVisibilityChange }: ControlsTabProps): JSX.Element => {
	const handleClearCookies = useCallback(() => {
		const cookies = document.cookie.split(';')
		const hostname = window.location.hostname
		const pathSegments = window.location.pathname.split('/')

		cookies.forEach(cookie => {
			const name = cookie.split('=')[0].trim()
			if (!name) return

			const domains = [
				'',
				hostname,
				`.${hostname}`,
				hostname.split('.').slice(-2).join('.'),
				`.${hostname.split('.').slice(-2).join('.')}`
			]

			const paths = ['/', '']
			pathSegments.reduce((acc, segment) => {
				if (segment) {
					acc += `/${segment}`
					paths.push(acc)
				}
				return acc
			}, '')

			domains.forEach(domain => {
				paths.forEach(path => {
					const domainPart = domain ? `;domain=${domain}` : ''
					const pathPart = path ? `;path=${path}` : ''
					document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT${pathPart}${domainPart}`
				})
			})
		})

		alert(`Cleared ${cookies.length} cookies`)
	}, [])

	const handleClearStorage = useCallback(() => {
		localStorage.clear()
		sessionStorage.clear()
		alert('localStorage and sessionStorage cleared')
	}, [])

	const handleFullReload = useCallback(() => {
		window.location.reload()
	}, [])

	const handleHardReload = useCallback(() => {
		window.location.href = window.location.href
	}, [])

	const handleToggleTab = (tab: keyof TabVisibility): void => {
		const newVisibility = { ...tabVisibility, [tab]: !tabVisibility[tab] }
		onTabVisibilityChange(newVisibility)
		setTabVisibilityCookie(newVisibility)
	}

	const checkboxStyle: React.CSSProperties = {
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
		padding: '8px 12px',
		backgroundColor: '#2d2d2d',
		borderRadius: '4px',
		cursor: 'pointer',
		color: '#fff',
		fontSize: '12px'
	}

	return (
		<div style={styles.controlsSection}>
			<div style={{ marginBottom: '12px' }}>
				<div style={{ color: '#888', fontSize: '11px', marginBottom: '8px', textTransform: 'uppercase' }}>
					Видимость вкладок
				</div>
				<div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
					<label style={checkboxStyle}>
						<input
							type="checkbox"
							checked={tabVisibility.logs}
							onChange={() => handleToggleTab('logs')}
							style={{ accentColor: '#007acc' }}
						/>
						Logs
					</label>
					<label style={checkboxStyle}>
						<input
							type="checkbox"
							checked={tabVisibility.network}
							onChange={() => handleToggleTab('network')}
							style={{ accentColor: '#007acc' }}
						/>
						Network
					</label>
					<label style={checkboxStyle}>
						<input
							type="checkbox"
							checked={tabVisibility.actions}
							onChange={() => handleToggleTab('actions')}
							style={{ accentColor: '#007acc' }}
						/>
						Actions
					</label>
					<label style={checkboxStyle}>
						<input
							type="checkbox"
							checked={tabVisibility.hydration}
							onChange={() => handleToggleTab('hydration')}
							style={{ accentColor: '#007acc' }}
						/>
						Hydration
					</label>
				</div>
			</div>
			<button style={styles.controlButton} onClick={handleClearCookies}>
				🍪 Почистить куки
			</button>
			<button style={styles.controlButton} onClick={handleClearStorage}>
				💾 Почистить localStorage & sessionStorage
			</button>
			<button style={styles.controlButton} onClick={handleFullReload}>
				🔄 Перезагрузить страницу
			</button>
			<button style={{ ...styles.controlButton, ...styles.controlButtonDanger }} onClick={handleHardReload}>
				⚡ Hard Reload (clear cache)
			</button>
			<button style={{ ...styles.controlButton, ...styles.controlButtonDanger }} onClick={disableDebugger}>
				🚫 Выключить дебаггер
			</button>
		</div>
	)
}

interface Size {
	width: number
	height: number
}

const MIN_WIDTH = 300
const MIN_HEIGHT = 200
const DEFAULT_WIDTH = 400
const DEFAULT_HEIGHT = 500

export const DebugPanel = (): JSX.Element | null => {
	const { state } = useDebugContext()
	const [isMinimized, setIsMinimized] = useState(true)
	const [activeTab, setActiveTab] = useState<TabType>('logs')
	const [position, setPosition] = useState<Position>({ x: 20, y: 20 })
	const [size, setSize] = useState<Size>({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT })
	const [isDragging, setIsDragging] = useState(false)
	const [isResizing, setIsResizing] = useState<string | null>(null)
	const [tabVisibility, setTabVisibility] = useState<TabVisibility>(DEFAULT_TAB_VISIBILITY)
	const dragOffset = useRef<Position>({ x: 0, y: 0 })
	const resizeStart = useRef<{ x: number; y: number; width: number; height: number }>({
		x: 0,
		y: 0,
		width: 0,
		height: 0
	})
	const panelRef = useRef<HTMLDivElement>(null)
	const [mounted, setMounted] = useState(false)

	useEffect(() => {
		setMounted(true)
		setTabVisibility(getTabVisibilityFromCookie())
	}, [])

	const handleMouseDown = useCallback((e: React.MouseEvent) => {
		if (panelRef.current) {
			const rect = panelRef.current.getBoundingClientRect()
			dragOffset.current = {
				x: e.clientX - rect.left,
				y: e.clientY - rect.top
			}
			setIsDragging(true)
		}
	}, [])

	const handleResizeStart = useCallback(
		(e: React.MouseEvent, direction: string) => {
			e.stopPropagation()
			e.preventDefault()
			resizeStart.current = {
				x: e.clientX,
				y: e.clientY,
				width: size.width,
				height: size.height
			}
			setIsResizing(direction)
		},
		[size]
	)

	useEffect(() => {
		if (!isDragging) return

		const handleMouseMove = (e: MouseEvent) => {
			setPosition({
				x: e.clientX - dragOffset.current.x,
				y: e.clientY - dragOffset.current.y
			})
		}

		const handleMouseUp = () => {
			setIsDragging(false)
		}

		document.addEventListener('mousemove', handleMouseMove)
		document.addEventListener('mouseup', handleMouseUp)

		return () => {
			document.removeEventListener('mousemove', handleMouseMove)
			document.removeEventListener('mouseup', handleMouseUp)
		}
	}, [isDragging])

	useEffect(() => {
		if (!isResizing) return

		const handleMouseMove = (e: MouseEvent) => {
			const deltaX = e.clientX - resizeStart.current.x
			const deltaY = e.clientY - resizeStart.current.y

			let newWidth = resizeStart.current.width
			let newHeight = resizeStart.current.height

			if (isResizing.includes('e')) {
				newWidth = Math.max(MIN_WIDTH, resizeStart.current.width + deltaX)
			}
			if (isResizing.includes('w')) {
				newWidth = Math.max(MIN_WIDTH, resizeStart.current.width - deltaX)
				if (newWidth !== resizeStart.current.width) {
					setPosition(prev => ({ ...prev, x: prev.x + (resizeStart.current.width - newWidth) }))
				}
			}
			if (isResizing.includes('s')) {
				newHeight = Math.max(MIN_HEIGHT, resizeStart.current.height + deltaY)
			}
			if (isResizing.includes('n')) {
				newHeight = Math.max(MIN_HEIGHT, resizeStart.current.height - deltaY)
				if (newHeight !== resizeStart.current.height) {
					setPosition(prev => ({ ...prev, y: prev.y + (resizeStart.current.height - newHeight) }))
				}
			}

			setSize({ width: newWidth, height: newHeight })
		}

		const handleMouseUp = () => {
			setIsResizing(null)
		}

		document.addEventListener('mousemove', handleMouseMove)
		document.addEventListener('mouseup', handleMouseUp)

		return () => {
			document.removeEventListener('mousemove', handleMouseMove)
			document.removeEventListener('mouseup', handleMouseUp)
		}
	}, [isResizing])

	const errorCount = useMemo(() => state.logs.filter(log => log.level === 'error').length, [state.logs])

	if (!mounted) return null

	const hydrationCount = state.hydrationErrors.length

	const allTabs: { key: TabType; label: string; visible: boolean }[] = [
		{ key: 'logs', label: `Logs (${state.logs.length})`, visible: tabVisibility.logs },
		{ key: 'network', label: `Network (${state.networkRequests.length})`, visible: tabVisibility.network },
		{ key: 'actions', label: `Actions (${state.userActions.length})`, visible: tabVisibility.actions },
		{
			key: 'hydration',
			label: `Hydration${hydrationCount > 0 ? ` ⚠️${hydrationCount}` : ''}`,
			visible: tabVisibility.hydration
		},
		{ key: 'controls', label: '⚙️', visible: true }
	]

	const tabs = allTabs.filter(tab => tab.visible)

	const content = isMinimized ? (
		<button data-debug-panel style={styles.minimizedButton} onClick={() => setIsMinimized(false)}>
			🐷
			{errorCount > 0 && <span style={styles.badge}>{errorCount}</span>}
		</button>
	) : (
		<div
			ref={panelRef}
			data-debug-panel
			style={{
				...styles.overlay,
				left: position.x,
				top: position.y,
				width: size.width,
				height: size.height
			}}>
			<div style={styles.panel}>
				<div style={styles.header} onMouseDown={handleMouseDown}>
					<span style={styles.headerTitle}>Дебаггер</span>
					<div style={styles.headerButtons}>
						<button style={styles.iconButton} onClick={() => setIsMinimized(true)} title="Minimize">
							Х
						</button>
					</div>
				</div>
				<div style={styles.tabs}>
					{tabs.map(tab => (
						<button
							key={tab.key}
							style={{
								...styles.tab,
								...(activeTab === tab.key ? styles.tabActive : {}),
								...(tab.key === 'controls' ? { marginLeft: 'auto' } : {})
							}}
							onClick={() => setActiveTab(tab.key)}>
							{tab.label}
						</button>
					))}
				</div>
				{activeTab === 'logs' && <LogsTab />}
				{activeTab === 'network' && <NetworkTab />}
				{activeTab === 'actions' && <ActionsTab />}
				{activeTab === 'hydration' && <HydrationTab />}
				{activeTab === 'controls' && (
					<ControlsTab tabVisibility={tabVisibility} onTabVisibilityChange={setTabVisibility} />
				)}
			</div>
			{/* Resize handles */}
			<div style={styles.resizeN} onMouseDown={e => handleResizeStart(e, 'n')} />
			<div style={styles.resizeS} onMouseDown={e => handleResizeStart(e, 's')} />
			<div style={styles.resizeE} onMouseDown={e => handleResizeStart(e, 'e')} />
			<div style={styles.resizeW} onMouseDown={e => handleResizeStart(e, 'w')} />
			<div style={styles.resizeNE} onMouseDown={e => handleResizeStart(e, 'ne')} />
			<div style={styles.resizeNW} onMouseDown={e => handleResizeStart(e, 'nw')} />
			<div style={styles.resizeSE} onMouseDown={e => handleResizeStart(e, 'se')} />
			<div style={styles.resizeSW} onMouseDown={e => handleResizeStart(e, 'sw')} />
		</div>
	)

	return createPortal(content, document.body)
}
